// @ts-nocheck
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

const TemplateCleanup: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ phase: string; count: number; success: boolean }[]>([]);

  const runCleanup = async () => {
    setLoading(true);
    setResults([]);
    const newResults: typeof results = [];

    try {
      // Phase 1: Disable duplicate upgrade templates
      const { data: phase1, error: error1 } = await supabase
        .from('communication_templates')
        .update({ is_active: false })
        .in('id', [
          '8376b985-fef1-445d-96b7-f325069ddb35',
          '56b7718b-b8d2-403b-99f3-ec83f8f0353a'
        ])
        .select('id');
      
      newResults.push({
        phase: "Disabled duplicate upgrade reminder templates",
        count: phase1?.length || 0,
        success: !error1
      });

      // Phase 2: Disable 14-day sequence templates
      const { data: phase2, error: error2 } = await supabase
        .from('communication_templates')
        .update({ is_active: false })
        .in('id', [
          'eb207876-6e49-40fb-82ad-7242061367e0',
          'bf07b203-d27a-46a7-8cd1-3342ae710260',
          '02eee7c9-65b6-4070-91ff-e4ddbec0a8a8'
        ])
        .select('id');
      
      newResults.push({
        phase: "Disabled 14-day email sequence templates",
        count: phase2?.length || 0,
        success: !error2
      });

      // Phase 3: Disable duplicate mention templates
      const { data: phase3, error: error3 } = await supabase
        .from('communication_templates')
        .update({ is_active: false })
        .in('id', [
          '7b32b90e-5d39-496e-9adb-35490474fbc8',
          'bd347156-af08-4ba5-b6ae-4b51aaf5aa27',
          '70080b82-7791-4595-84e9-f0e49008c247',
          '8b191668-899d-4b08-b3bc-58369db09879'
        ])
        .select('id');
      
      newResults.push({
        phase: "Disabled duplicate article/media mention templates",
        count: phase3?.length || 0,
        success: !error3
      });

      // Phase 4: Disable unused notification templates
      const { data: phase4, error: error4 } = await supabase
        .from('communication_templates')
        .update({ is_active: false })
        .in('name', [
          'Communication Processing',
          'Event Notifications',
          'Media Credentials Request',
          'Default Email Header',
          'Default Email Footer'
        ])
        .select('id');
      
      newResults.push({
        phase: "Disabled unused notification templates",
        count: phase4?.length || 0,
        success: !error4
      });

      // Phase 5: Fix event assignments - Coach Profile View
      const { data: coachViewEvent } = await supabase
        .from('communication_events')
        .select('id')
        .eq('slug', 'coach_profile_view')
        .single();

      if (coachViewEvent) {
        const { data: phase5, error: error5 } = await supabase
          .from('communication_templates')
          .update({ trigger_event: coachViewEvent.id })
          .eq('name', 'Coach Profile View')
          .select('id');
        
        newResults.push({
          phase: "Fixed Coach Profile View event assignment",
          count: phase5?.length || 0,
          success: !error5
        });
      }

      // Phase 6: Fix event assignments - Article Mention
      const { data: articleMentionEvent } = await supabase
        .from('communication_events')
        .select('id')
        .eq('slug', 'article_mention')
        .single();

      if (articleMentionEvent) {
        const { data: phase6, error: error6 } = await supabase
          .from('communication_templates')
          .update({ trigger_event: articleMentionEvent.id })
          .like('name', '%Article Mention%')
          .eq('is_active', true)
          .select('id');
        
        newResults.push({
          phase: "Fixed Article Mention event assignments",
          count: phase6?.length || 0,
          success: !error6
        });
      }

      // Phase 7: Ensure critical templates are active
      const { data: phase7, error: error7 } = await supabase
        .from('communication_templates')
        .update({ is_active: true })
        .in('name', [
          'Athlete Premium Welcome',
          'Admin: New Athlete Signup',
          'Admin: New User Signup',
          'Password Reset Request',
          'Coach Profile View',
          'Subscription Renewal Reminder',
          'Gift Membership Notification'
        ])
        .eq('is_active', false)
        .select('id');
      
      newResults.push({
        phase: "Re-enabled critical templates",
        count: phase7?.length || 0,
        success: !error7
      });

      setResults(newResults);
      
      const successCount = newResults.filter(r => r.success).length;
      if (successCount === newResults.length) {
        toast.success(`✅ Cleanup complete! Updated ${newResults.reduce((sum, r) => sum + r.count, 0)} templates across ${newResults.length} phases.`);
      } else {
        toast.warning(`⚠️ Cleanup partially complete. ${successCount}/${newResults.length} phases succeeded.`);
      }

    } catch (error: any) {
      console.error('Cleanup error:', error);
      toast.error(error.message || 'Failed to clean up templates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Template Cleanup Utility</h1>
        <p className="text-muted-foreground">
          Clean up duplicate and misconfigured email templates
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>One-Time Template Cleanup</CardTitle>
          <CardDescription>
            This will:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Disable 2 duplicate upgrade reminder templates</li>
              <li>Disable 3 old 14-day sequence templates (use Email Sequences instead)</li>
              <li>Disable 4 duplicate article/media mention templates</li>
              <li>Disable 5 unused notification templates</li>
              <li>Fix event assignments for Coach Profile View</li>
              <li>Fix event assignments for Article Mentions</li>
              <li>Ensure 7 critical templates remain active</li>
            </ul>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={runCleanup} 
            disabled={loading}
            size="lg"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Running Cleanup...' : 'Run Template Cleanup'}
          </Button>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cleanup Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {results.map((result, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-md">
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="text-sm">{result.phase}</span>
                  </div>
                  <Badge variant={result.success ? "default" : "destructive"}>
                    {result.count} templates
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TemplateCleanup;
